/**
 * useGoals / useGoal — the React bindings for the goals feature.
 *
 * Strict layering: pages call THESE hooks; the hooks call {@link GoalStore}
 * (and, for the detail rollup, the existing {@link TaskStore} /
 * {@link EstimateStore}); the stores call the api facade. Pages never import a
 * store or the api directly.
 *
 * - {@link useGoals} owns the goal LIST (summaries) + create/update/remove, and
 *   powers both the list view and the hierarchy tree.
 * - {@link useGoal} owns ONE goal's detail: it hydrates the full document, loads
 *   the workspace's tasks + estimate summaries once, and derives the
 *   traceability rollup and the advisory Goal Health signal CLIENT-SIDE. It
 *   also exposes save (edit) and applyStatus (the human override of the health
 *   recommendation).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { TaskStore } from '../tasks/taskStore';
import { EstimateStore } from '../estimation/estimateStore';
import { GoalStore } from './goalStore';
import { sortGoals } from './goalModel';
import { computeTraceability, type GoalTraceability } from './traceability';
import { computeGoalHealth, type GoalHealthSignal } from './goalHealth';
import {
  goalProgress as computeGoalProgress,
  type GoalProgress,
} from '../sprintboard/engine/aggregation';
import type { Goal, GoalDraft, GoalStatus, GoalSummary } from './types';
import type { Task } from '../tasks/types';
import type { EstimateSummary } from '../estimation/types';

// ---------------------------------------------------------------------------
// useGoals — list + mutations
// ---------------------------------------------------------------------------

export interface UseGoalsResult {
  /** All non-deleted goal summaries (level- then recency-sorted). */
  goals: GoalSummary[];
  loading: boolean;
  error: string | null;
  /** Re-fetches the goal list. */
  refresh: () => Promise<void>;
  /** Creates a goal from a draft. */
  create: (draft: GoalDraft) => Promise<Goal>;
  /** Soft-deletes a goal. */
  remove: (id: string) => Promise<void>;
}

export function useGoals(): UseGoalsResult {
  const api = useApi();
  const store = useMemo(() => new GoalStore(api), [api]);

  const [goals, setGoals] = useState<GoalSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await store.listSummaries();
      setGoals(sortGoals(rows));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load goals.');
    } finally {
      setLoading(false);
    }
  }, [store]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const create = useCallback(
    async (draft: GoalDraft) => {
      const saved = await store.create(draft);
      await refresh();
      return saved;
    },
    [store, refresh],
  );

  const remove = useCallback(
    async (id: string) => {
      await store.softDelete(id);
      await refresh();
    },
    [store, refresh],
  );

  return { goals, loading, error, refresh, create, remove };
}

// ---------------------------------------------------------------------------
// useGoal — single-goal detail with client-side traceability + health
// ---------------------------------------------------------------------------

export interface UseGoalResult {
  /** The hydrated goal document, or null when not loaded / not found. */
  goal: Goal | null;
  /** All goal summaries (for parent/related/child resolution in the UI). */
  allGoals: GoalSummary[];
  /** Client-side engineering-work rollup for this goal. */
  traceability: GoalTraceability | null;
  /**
   * Goal progress aggregated from tasks linked to this goal via `task.goalId`
   * (Phase-1 aggregation) — effort + completion of the goal's sprint tasks.
   */
  sprintTaskProgress: GoalProgress | null;
  /** Advisory Goal Health signal (never auto-applied). */
  health: GoalHealthSignal | null;
  loading: boolean;
  error: string | null;
  /** Re-loads the goal + rollup inputs. */
  refresh: () => Promise<void>;
  /** Applies an edited draft to the goal. */
  save: (draft: GoalDraft) => Promise<Goal>;
  /** Persists a human status override (+ optional note). */
  applyStatus: (status: GoalStatus, note?: string) => Promise<Goal>;
}

export function useGoal(goalId: string | undefined): UseGoalResult {
  const api = useApi();
  const stores = useMemo(
    () => ({
      goals: new GoalStore(api),
      tasks: new TaskStore(api),
      estimates: new EstimateStore(api),
    }),
    [api],
  );

  const [goal, setGoal] = useState<Goal | null>(null);
  const [allGoals, setAllGoals] = useState<GoalSummary[]>([]);
  const [traceability, setTraceability] = useState<GoalTraceability | null>(
    null,
  );
  const [sprintTaskProgress, setSprintTaskProgress] =
    useState<GoalProgress | null>(null);
  const [health, setHealth] = useState<GoalHealthSignal | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const recompute = useCallback(
    (g: Goal, allTasks: Task[], allEstimates: EstimateSummary[]) => {
      const trace = computeTraceability(g, allTasks, allEstimates);
      setTraceability(trace);
      setSprintTaskProgress(computeGoalProgress(g.id, allTasks));
      setHealth(computeGoalHealth(g, trace));
    },
    [],
  );

  const refresh = useCallback(async () => {
    if (!goalId) {
      setGoal(null);
      setTraceability(null);
      setSprintTaskProgress(null);
      setHealth(null);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [loaded, summaries, allTasks, allEstimates] = await Promise.all([
        stores.goals.get(goalId),
        stores.goals.listSummaries(),
        stores.tasks.listAll(),
        stores.estimates.listSummaries(),
      ]);
      setAllGoals(summaries);
      if (!loaded || loaded.deleted) {
        setGoal(null);
        setTraceability(null);
        setSprintTaskProgress(null);
        setHealth(null);
        setError('Goal not found.');
        return;
      }
      setGoal(loaded);
      recompute(loaded, allTasks, allEstimates);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load goal.');
    } finally {
      setLoading(false);
    }
  }, [goalId, stores, recompute]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const save = useCallback(
    async (draft: GoalDraft) => {
      if (!goal) throw new Error('No goal loaded to save.');
      const saved = await stores.goals.applyDraft(goal, draft);
      await refresh();
      return saved;
    },
    [goal, stores, refresh],
  );

  const applyStatus = useCallback(
    async (status: GoalStatus, note?: string) => {
      if (!goal) throw new Error('No goal loaded to update.');
      const saved = await stores.goals.setStatus(goal, status, note);
      await refresh();
      return saved;
    },
    [goal, stores, refresh],
  );

  return {
    goal,
    allGoals,
    traceability,
    sprintTaskProgress,
    health,
    loading,
    error,
    refresh,
    save,
    applyStatus,
  };
}
